/**
 * ScheduleRunStepper — compact horizontal phase stepper for pipeline runs.
 *
 * Shows the progression through pipeline phases:
 *   Ingest → Profile → Check → Summarize → Map → Validate → Load → Done
 *
 * Each step: pending (grey) → running (spinner) → completed (✓) → failed (✗) → skipped (—)
 */
import React from 'react';
import {
  Box,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as DoneIcon,
  Error as ErrorIcon,
  RemoveCircleOutline as SkippedIcon,
  RadioButtonUnchecked as PendingIcon,
} from '@mui/icons-material';
import type { ScheduleProgressState } from '../../context/NotificationContext';

/** Ordered phases as emitted by the pipeline runner. */
const PHASES = [
  { key: 'validation', label: 'Validate' },
  { key: 'batch_creation', label: 'Ingest' },
  { key: 'profiling', label: 'Profile' },
  { key: 'compatibility_check', label: 'Check' },
  { key: 'summarization', label: 'Summarize' },
  { key: 'recipe_apply', label: 'Map' },
  { key: 'schema_drift', label: 'Drift' },
  { key: 'export_validation', label: 'Validate' },
  { key: 'loading', label: 'Load' },
  { key: 'completed', label: 'Done' },
] as const;

type PhaseKey = (typeof PHASES)[number]['key'];

type PhaseStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';

interface Props {
  /** Current run phase (from DB poll or SSE). */
  currentPhase?: string;
  /** Run status — if "completed" all steps are done; if "failed" the current phase is marked failed. */
  runStatus?: string;
  /** Live SSE progress (optional, enhances with metrics/logLine). */
  liveProgress?: ScheduleProgressState | null;
}

function getPhaseStatus(
  phaseKey: PhaseKey,
  currentPhase: string | undefined,
  runStatus: string | undefined,
  liveProgress: ScheduleProgressState | null | undefined,
): PhaseStatus {
  const phaseIdx = PHASES.findIndex((p) => p.key === phaseKey);
  const currentIdx = PHASES.findIndex((p) => p.key === currentPhase);

  // Check if live progress says this specific phase was skipped
  if (liveProgress?.phase === phaseKey && liveProgress.phaseStatus === 'skipped') {
    return 'skipped';
  }

  // Run completed — all phases that ran are done
  if (runStatus === 'completed') {
    return phaseKey === 'completed' ? 'completed' : (phaseIdx <= currentIdx ? 'completed' : 'skipped');
  }

  // Run failed — phases before current are done, current is failed
  if (runStatus === 'failed' || runStatus === 'skipped') {
    if (phaseIdx < currentIdx) return 'completed';
    if (phaseIdx === currentIdx) return 'failed';
    return 'pending';
  }

  // Running — phases before current are done, current is running
  if (phaseIdx < currentIdx) return 'completed';
  if (phaseIdx === currentIdx) return 'running';
  return 'pending';
}

function StepIcon({ status }: { status: PhaseStatus }) {
  switch (status) {
    case 'completed':
      return <DoneIcon sx={{ color: 'success.main', fontSize: 18 }} />;
    case 'running':
      return <CircularProgress size={16} />;
    case 'failed':
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />;
    case 'skipped':
      return <SkippedIcon sx={{ color: 'text.disabled', fontSize: 18 }} />;
    default:
      return <PendingIcon sx={{ color: 'text.disabled', fontSize: 18 }} />;
  }
}

export default function ScheduleRunStepper({ currentPhase, runStatus, liveProgress }: Props) {
  return (
    <Box sx={{ py: 1 }}>
      <Stepper alternativeLabel sx={{ '& .MuiStepConnector-line': { minWidth: 16 } }}>
        {PHASES.map((phase) => {
          const status = getPhaseStatus(phase.key, currentPhase, runStatus, liveProgress);
          return (
            <Step key={phase.key} completed={status === 'completed'}>
              <StepLabel
                StepIconComponent={() => <StepIcon status={status} />}
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: 10,
                    color: status === 'skipped' ? 'text.disabled' : undefined,
                    textDecoration: status === 'skipped' ? 'line-through' : undefined,
                  },
                }}
              >
                <Tooltip title={phase.key} arrow>
                  <span>{phase.label}</span>
                </Tooltip>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {/* Current log line from SSE */}
      {liveProgress?.logLine && runStatus === 'running' && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: 'block', textAlign: 'center', fontStyle: 'italic' }}
        >
          {liveProgress.logLine}
        </Typography>
      )}
    </Box>
  );
}
